import axios from 'axios';
import { API_URL } from '../../../../support/api';
import { getCached } from '../../../../support/cache';
import { PaginatedValue } from '../../../../support/utils';

/**
 * Dashboard aggregate counts.
 *
 * Each count is read from the relevant module's paginated `list` endpoint with
 * `paginate=1`: we only care about the `total` of the paginator, not the rows,
 * so we ask the backend for the smallest possible page.
 */
export type DashboardStats = {
  pages: number;
  posts: number;
  users: number;
  files: number;
  /** Total number of quotations. */
  quotes: number;
};

export const DASHBOARD_STATS_CACHE_KEY = 'dashboard:stats';

// Smallest page possible — we read `total`, not the data rows.
const COUNT_QUERY = 'paginate=1';

export const PAGES_COUNT_URL = `${API_URL}/v1/page-management/pages/list?${COUNT_QUERY}`;
export const POSTS_COUNT_URL = `${API_URL}/v1/post-management/posts/list?${COUNT_QUERY}`;
export const USERS_COUNT_URL = `${API_URL}/v1/user-management/users?${COUNT_QUERY}`;
export const FILES_COUNT_URL = `${API_URL}/v1/media-library/list?${COUNT_QUERY}`;
export const QUOTATIONS_COUNT_URL = `${API_URL}/v1/quotation/management/quotations/list?${COUNT_QUERY}`;

/** Fetch the `total` from a paginated list endpoint. */
export const fetchCount = async (url: string): Promise<number> => {
  const {
    data: { value },
  } = await axios.get<{ value: PaginatedValue<unknown> }>(url);

  return value.total;
};

/** Fetch all dashboard counts in parallel. */
export const fetchDashboardStats = async (): Promise<DashboardStats> => {
  const [pages, posts, users, files, quotes] = await Promise.all([
    fetchCount(PAGES_COUNT_URL),
    fetchCount(POSTS_COUNT_URL),
    fetchCount(USERS_COUNT_URL),
    fetchCount(FILES_COUNT_URL),
    fetchCount(QUOTATIONS_COUNT_URL),
  ]);

  return { pages, posts, users, files, quotes };
};

/**
 * Cached entry point used by the dashboard. The result is memoised so that
 * navigating back to the dashboard does not re-query the database on every
 * mount. Pass `force` to bypass the cache (e.g. an explicit refresh).
 */
export const getDashboardStats = (force = false): Promise<DashboardStats> => {
  if (force) {
    return fetchDashboardStats();
  }

  return getCached(DASHBOARD_STATS_CACHE_KEY, fetchDashboardStats);
};
